import { FormEvent, MouseEvent, ReactNode, useEffect, useState } from "react";
import { OAuthClient, OAuthClientCreated, api } from "../api";
import { useAuth } from "../auth";

const MCP_URL = `${window.location.origin}/mcp/`;

type ConfirmState = {
  title: string;
  tone: "danger" | "primary";
  confirmLabel: string;
  body: ReactNode;
  onConfirm: () => Promise<void>;
};

export function OAuthClientsPage() {
  const { token } = useAuth();
  const [clients, setClients] = useState<OAuthClient[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const [clientName, setClientName] = useState("");
  const [redirectUris, setRedirectUris] = useState("");
  const [formBusy, setFormBusy] = useState(false);
  const [formNotice, setFormNotice] = useState<{ tone: "ok" | "err"; text: string } | null>(null);

  // The one-time secret reveal. Cleared once the admin dismisses it.
  const [revealed, setRevealed] = useState<OAuthClientCreated | null>(null);
  // In-page confirmation dialog (replaces window.confirm).
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  const reload = () => {
    if (!token) return;
    api
      .listOAuthClients(token)
      .then(setClients)
      .catch((e) => setError(String(e?.message ?? e)));
  };

  useEffect(reload, [token]);

  const resetForm = () => {
    setClientName("");
    setRedirectUris("");
    setFormNotice(null);
  };

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setFormNotice(null);
    const name = clientName.trim();
    if (name.length < 2) {
      setFormNotice({ tone: "err", text: "Client name must be at least 2 characters." });
      return;
    }
    const uris = redirectUris
      .split(/[\n,]/)
      .map((u) => u.trim())
      .filter(Boolean);
    setFormBusy(true);
    try {
      const created = await api.createOAuthClient(token, {
        client_name: name,
        redirect_uris: uris.length ? uris : null,
        is_confidential: true,
      });
      setRevealed(created);
      reload();
      resetForm();
    } catch (err: unknown) {
      const message =
        typeof err === "object" && err && "message" in err
          ? String((err as { message?: string }).message)
          : String(err);
      setFormNotice({ tone: "err", text: message });
    } finally {
      setFormBusy(false);
    }
  };

  const onRotate = (c: OAuthClient) => {
    if (!token) return;
    setConfirm({
      title: "Rotate client secret",
      tone: "primary",
      confirmLabel: "Rotate secret",
      body: (
        <>
          <p>
            Rotate the secret for <strong>{c.client_name}</strong>{" "}
            (<code className="font-mono text-xs">{c.client_id}</code>)?
          </p>
          <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 text-xs">
            The current secret stops working <strong>immediately</strong>. You must paste the new
            secret into Claude's connector. The new secret is shown only once.
          </p>
        </>
      ),
      onConfirm: async () => {
        setBusyId(c.id);
        try {
          const rotated = await api.rotateOAuthClientSecret(token, c.id);
          setRevealed(rotated);
          reload();
        } finally {
          setBusyId(null);
        }
      },
    });
  };

  const onDelete = (c: OAuthClient) => {
    if (!token) return;
    setConfirm({
      title: "Delete OAuth client",
      tone: "danger",
      confirmLabel: "Delete client",
      body: (
        <>
          <p>
            Delete OAuth client <strong>{c.client_name}</strong>{" "}
            (<code className="font-mono text-xs">{c.client_id}</code>)?
          </p>
          <p className="text-red-800 bg-red-50 border border-red-200 rounded px-3 py-2 text-xs">
            Any Claude connector using this client will stop working immediately. This cannot be
            undone.
          </p>
        </>
      ),
      onConfirm: async () => {
        setBusyId(c.id);
        try {
          await api.deleteOAuthClient(token, c.id);
          reload();
        } finally {
          setBusyId(null);
        }
      },
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">OAuth management</h2>
      </div>

      <div className="card p-4 space-y-2 text-sm">
        <p className="text-slate-700">
          Register a Claude <strong>custom connector</strong> once with these values. Each analyst
          then signs in on this server with their own MCP account; Claude receives per-user tokens.
        </p>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-slate-600 w-28">MCP server URL</span>
          <CopyField value={MCP_URL} mono />
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>
      )}

      {revealed && (
        <div className="card p-4 space-y-3 border-2 border-emerald-300 bg-emerald-50/60">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-emerald-900">
              Copy the secret now — it is shown only once
            </h3>
            <button type="button" className="btn" onClick={() => setRevealed(null)}>
              Done
            </button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-slate-600 w-28">Client ID</span>
            <CopyField value={revealed.client_id} mono />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-slate-600 w-28">Client Secret</span>
            <CopyField value={revealed.client_secret ?? ""} mono />
          </div>
          <p className="text-xs text-emerald-800">
            Paste both into Claude → Connectors → Custom → Web → Advanced settings. If lost, use
            <strong> rotate secret</strong> (the old one is invalidated).
          </p>
        </div>
      )}

      <div className="card p-4 space-y-3">
        <h3 className="text-sm font-semibold text-slate-900">Create OAuth client</h3>
        <form className="space-y-3" onSubmit={(e) => void onCreate(e)}>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Client name</label>
            <input
              className="input"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Claude Web Connector"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Redirect URIs <span className="text-slate-400">(optional — one per line; defaults to Claude)</span>
            </label>
            <textarea
              className="input font-mono text-xs min-h-[3.5rem]"
              value={redirectUris}
              onChange={(e) => setRedirectUris(e.target.value)}
              placeholder={"https://claude.ai/api/mcp/auth_callback\nhttps://claude.com/api/mcp/auth_callback"}
            />
          </div>
          {formNotice && (
            <div
              className={`text-sm rounded px-3 py-2 border ${
                formNotice.tone === "ok"
                  ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                  : "bg-red-50 text-red-800 border-red-200"
              }`}
            >
              {formNotice.text}
            </div>
          )}
          <div className="flex gap-2">
            <button type="submit" className="btn-primary" disabled={formBusy}>
              {formBusy ? "Creating…" : "Create client"}
            </button>
            <button type="button" className="btn" disabled={formBusy} onClick={resetForm}>
              Reset fields
            </button>
          </div>
        </form>
      </div>

      <div className="card overflow-hidden">
        {clients === null ? (
          <p className="p-4 text-sm text-slate-500">Loading…</p>
        ) : clients.length === 0 ? (
          <p className="p-4 text-sm text-slate-500">No OAuth clients yet.</p>
        ) : (
          <table className="table-clean table-fixed w-full">
            <thead>
              <tr>
                <th className="w-10">id</th>
                <th className="w-40">name</th>
                <th className="w-56">client_id</th>
                <th className="w-44">redirect_uris</th>
                <th className="w-24">type</th>
                <th className="text-right">actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} className="border-t border-slate-100 align-middle">
                  <td className="font-mono">{c.id}</td>
                  <td className="truncate" title={c.client_name}>{c.client_name}</td>
                  <td className="text-xs">
                    <CopyField value={c.client_id} mono compact />
                  </td>
                  <td className="text-xs overflow-hidden">
                    <RedirectUrisCell uris={c.redirect_uris} />
                  </td>
                  <td className="text-xs whitespace-nowrap">
                    {c.is_confidential ? "confidential" : "public"}
                    {c.disabled && <span className="text-red-600"> (disabled)</span>}
                  </td>
                  <td className="text-right space-x-2 whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => onRotate(c)}
                      disabled={busyId === c.id}
                      className="btn"
                    >
                      rotate secret
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(c)}
                      disabled={busyId === c.id}
                      className="btn-danger"
                    >
                      delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {confirm && <ConfirmModal state={confirm} onClose={() => setConfirm(null)} />}
    </div>
  );
}

function ConfirmModal({ state, onClose }: { state: ConfirmState; onClose: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onBackdrop = (ev: MouseEvent<HTMLDivElement>) => {
    if (ev.target === ev.currentTarget && !busy) onClose();
  };

  const onConfirm = async () => {
    setError(null);
    setBusy(true);
    try {
      await state.onConfirm();
      onClose();
    } catch (e: unknown) {
      const msg =
        typeof e === "object" && e && "message" in e ? String((e as { message?: string }).message) : String(e);
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={onBackdrop}
      role="presentation"
    >
      <div
        className="bg-white rounded-lg border border-slate-200 shadow-xl max-w-md w-full flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="oauth-confirm-title"
        onMouseDown={(ev) => ev.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3
            id="oauth-confirm-title"
            className={`text-base font-semibold ${state.tone === "danger" ? "text-red-700" : "text-slate-900"}`}
          >
            {state.title}
          </h3>
          <button type="button" onClick={onClose} className="btn shrink-0" disabled={busy} aria-label="Close">
            Close
          </button>
        </div>

        <div className="p-4 space-y-3 text-sm text-slate-700">
          {state.body}
          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-slate-100 flex justify-end gap-2 bg-slate-50 rounded-b-lg">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className={state.tone === "danger" ? "btn-danger" : "btn-primary"}
            disabled={busy}
            onClick={() => void onConfirm()}
          >
            {busy ? "Working…" : state.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function RedirectUrisCell({ uris }: { uris: string[] }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  if (uris.length === 0) return <span className="text-slate-400">—</span>;
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(uris.join("\n"));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard may be blocked */
    }
  };
  return (
    <div className="flex flex-col gap-1 min-w-0">
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="text-[11px] text-slate-600 whitespace-nowrap" title={uris.join("\n")}>
          {uris.length} URI{uris.length > 1 ? "s" : ""}
        </span>
        <button type="button" onClick={() => void onCopy()} className="btn text-[10px] py-0.5 px-1.5" title="Copy all redirect URIs">
          {copied ? "copied" : "copy"}
        </button>
        <button type="button" onClick={() => setOpen((v) => !v)} className="btn text-[10px] py-0.5 px-1.5">
          {open ? "hide" : "show"}
        </button>
      </div>
      {open && (
        <div className="flex flex-col gap-1">
          {uris.map((u) => (
            <code
              key={u}
              className="font-mono text-[11px] text-slate-600 bg-slate-100 rounded px-1.5 py-0.5 block truncate"
              title={u}
            >
              {u}
            </code>
          ))}
        </div>
      )}
    </div>
  );
}

function CopyField({ value, mono = false, compact = false }: { value: string; mono?: boolean; compact?: boolean }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      // ignore — clipboard may be blocked without focus
    }
  };
  return (
    <span className="inline-flex items-center gap-2 min-w-0">
      <code
        className={`${mono ? "font-mono" : ""} ${
          compact ? "max-w-[14rem] truncate" : ""
        } text-[12px] text-slate-700 bg-slate-100 rounded px-2 py-1`}
        title={value}
      >
        {value || "—"}
      </code>
      <button type="button" onClick={() => void onCopy()} className="btn text-[10px] py-0.5 px-1.5">
        {copied ? "copied" : "copy"}
      </button>
    </span>
  );
}
